import type { MapObject, RoomKey, RoomKeyMap, RoomKeySenses } from "@/src/types/map";

/**
 * Adventure keying, Slice A.
 *
 * A room key binds to the stable id of a room-number annotation object. The
 * number the panel shows is derived from that annotation every time it is read,
 * so renumbering a room never has to touch the key. Deleting the annotation
 * leaves the key behind as an *orphan* rather than deleting it. See D-025.
 */

/** The annotation asset a room key binds to. */
export const ROOM_KEY_ANCHOR_ASSET = "room_number" as const;

/** The plain-text fields of a room key. Order matters: it is the panel's order. */
export const ROOM_KEY_TEXT_FIELDS = [
  "name",
  "description",
  "readAloud",
  "lighting",
  "dmNotes",
] as const;

export type RoomKeyTextField = (typeof ROOM_KEY_TEXT_FIELDS)[number];

/** The four senses beyond sight, in panel order. */
export const ROOM_KEY_SENSE_FIELDS = ["smell", "sound", "temperature", "texture"] as const;

export type RoomKeySenseField = (typeof ROOM_KEY_SENSE_FIELDS)[number];

/**
 * Fields that carry DM-only content. The player-view filter is Phase 5; this
 * list is what that filter will read, and shipping it now means the data is
 * already marked correctly when the filter arrives (D-019 precedent).
 */
export const ROOM_KEY_DM_ONLY_FIELDS: readonly RoomKeyTextField[] = ["dmNotes"];

/** Every field name a room key may legally carry. Anything else is rejected. */
export const ROOM_KEY_FIELDS: readonly string[] = [
  ...ROOM_KEY_TEXT_FIELDS,
  "senses",
  "extensions",
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isOptionalString = (value: unknown): boolean =>
  value === undefined || typeof value === "string";

export const isRoomKeySenses = (value: unknown): value is RoomKeySenses =>
  isPlainObject(value) &&
  Object.keys(value).every((key) => (ROOM_KEY_SENSE_FIELDS as readonly string[]).includes(key)) &&
  ROOM_KEY_SENSE_FIELDS.every((field) => isOptionalString(value[field]));

export const isRoomKey = (value: unknown): value is RoomKey =>
  isPlainObject(value) &&
  Object.keys(value).every((key) => ROOM_KEY_FIELDS.includes(key)) &&
  ROOM_KEY_TEXT_FIELDS.every((field) => isOptionalString(value[field])) &&
  (value.senses === undefined || isRoomKeySenses(value.senses)) &&
  // Extensions are validated as STRUCTURE and never as content: the whole point
  // of the bag is that a key written by a later slice survives a trip through
  // this one untouched. Everything outside it stays strictly closed.
  (value.extensions === undefined || isPlainObject(value.extensions));

/**
 * A key whose binding id matches no object in the document is an orphan, which
 * is a legal state rather than a validation failure — that is exactly what
 * deleting a room number produces.
 */
export const isRoomKeyMap = (value: unknown): value is RoomKeyMap =>
  isPlainObject(value) &&
  Object.entries(value).every(([objectId, key]) => objectId.length > 0 && isRoomKey(key));

/**
 * The number the map shows for this room, read live off the annotation. Null
 * when the binding id resolves to nothing, or to something that is not a
 * room-number annotation.
 */
export const roomNumberFor = (objectId: string, objects: MapObject[]): string | null => {
  const anchor = objects.find((object) => object.id === objectId);
  if (!anchor || anchor.assetId !== ROOM_KEY_ANCHOR_ASSET) return null;
  return anchor.text ?? "";
};

/** True when nothing in the document carries this key's binding id any more. */
export const isOrphanedRoomKey = (objectId: string, objects: MapObject[]): boolean =>
  !objects.some((object) => object.id === objectId);

export interface OrphanedRoomKey {
  objectId: string;
  key: RoomKey;
}

/** Orphans in insertion order, so the list does not reshuffle between renders. */
export const orphanedRoomKeys = (
  roomKeys: RoomKeyMap,
  objects: MapObject[],
): OrphanedRoomKey[] => {
  const live = new Set(objects.map((object) => object.id));
  return Object.entries(roomKeys)
    .filter(([objectId]) => !live.has(objectId))
    .map(([objectId, key]) => ({ objectId, key }));
};

export const ROOM_KEY_SNIPPET_LENGTH = 80;

/**
 * A one-line preview of whatever the key actually says, for the orphan list.
 * Prose first, then senses, then lighting — the fields a DM is most likely to
 * recognise the room by. Empty when the key holds no text at all.
 */
export const roomKeySnippet = (key: RoomKey, limit: number = ROOM_KEY_SNIPPET_LENGTH): string => {
  const candidates: (string | undefined)[] = [
    key.description,
    key.readAloud,
    key.dmNotes,
    ...ROOM_KEY_SENSE_FIELDS.map((field) => key.senses?.[field]),
    key.lighting,
  ];
  const source = candidates.find((value) => value !== undefined && value.trim().length > 0)?.trim();
  if (!source) return "";
  return source.length <= limit ? source : `${source.slice(0, limit - 1).trimEnd()}…`;
};

/** The shape the editing panel commits. Extensions are not editable in Slice A. */
export type RoomKeyUpdate = Partial<Record<RoomKeyTextField, string>> & {
  senses?: Partial<RoomKeySenses>;
};

/**
 * Applies an update to a key field by field.
 *
 * Deliberately NOT `{ ...existing, ...updates }`: extensions and any field the
 * update does not mention must survive untouched, and a per-field copy makes
 * that structural rather than a property of how callers happen to build updates.
 */
export const mergeRoomKey = (existing: RoomKey | undefined, updates: RoomKeyUpdate): RoomKey => {
  const merged: RoomKey = { ...existing };
  for (const field of ROOM_KEY_TEXT_FIELDS) {
    const value = updates[field];
    if (value !== undefined) merged[field] = value;
  }
  if (updates.senses !== undefined) {
    merged.senses = { ...merged.senses };
    for (const field of ROOM_KEY_SENSE_FIELDS) {
      const value = updates.senses[field];
      if (value !== undefined) merged.senses[field] = value;
    }
  }
  return merged;
};

/**
 * Field-by-field equality, used as the store's no-op guard.
 *
 * Blur fires whether or not anything was typed, so committing an identical key
 * would burn an undo step per focus. Extensions compare by JSON because their
 * content is deliberately unmodelled; `mergeRoomKey` never reorders them, so a
 * key-order difference cannot arise from an edit.
 */
export const roomKeysEqual = (a: RoomKey, b: RoomKey): boolean =>
  ROOM_KEY_TEXT_FIELDS.every((field) => a[field] === b[field]) &&
  ROOM_KEY_SENSE_FIELDS.every((field) => a.senses?.[field] === b.senses?.[field]) &&
  JSON.stringify(a.extensions ?? null) === JSON.stringify(b.extensions ?? null);

/** Deep copy for the undo snapshot: keys must not share nested objects with it. */
export const cloneRoomKeys = (roomKeys: RoomKeyMap): RoomKeyMap =>
  Object.fromEntries(
    Object.entries(roomKeys).map(([objectId, key]) => [
      objectId,
      {
        ...key,
        ...(key.senses === undefined ? {} : { senses: { ...key.senses } }),
        ...(key.extensions === undefined ? {} : { extensions: structuredClone(key.extensions) }),
      },
    ]),
  );
